<template>
  <main class="task-page">
    <div class="dashboard-toolbar">
      <form v-if="!isReadOnly" @submit.prevent="newTask">
        <MainButton :button-text="$t(`taskPage.createTask`)"
                    type="submit"
                    style="height:50px;margin:1rem; width:200px"
        />
      </form>
      <SearchBox v-if="showReports"
                 v-model="taskSearch"
                 :placeholder="$t('dashboardPage.searchTasks')"
                 @search="onTaskSearch"
      />
    </div>

    <div class="grid-container"
         :class="{ 'with-reports': showReports }"
    >
      <!-- UK/GB02: the reports take a full width row above the task lists -->
      <div v-if="showReports"
           class="grid-item report-section"
      >
        <ReportList />
      </div>

      <!-- Takes no part in the layout, its sections are the grid items -->
      <div class="dashboard-sections">
        <div class="grid-item task-list area-active">
          <div style="height:10px" />
          <PaginatedList :tasks="activeTasks"
                         :header-text="$t('dashboardPage.activeTasks')"
                         show-header="true"
                         :table-columns="activeTaskColumns"
                         :loading="isActiveLoading"
                         :current-page="activeCurrentPage"
                         :total-pages="activeTotalPages"
                         :total-count="activeTotalCount"
                         :page-size="activePageSize"
                         :show-status-filters="false"
                         preference-key="dashboard.activeTasks"
                         @update="fetchActiveTasks"
          />
        </div>

        <div class="grid-item task-list area-finished">
          <div style="height:10px" />
          <PaginatedList :tasks="finishedTasks"
                         :header-text="$t('dashboardPage.finishedTasks')"
                         show-header="true"
                         :table-columns="finishedTaskColumns"
                         :loading="isFinishedLoading"
                         :current-page="finishedCurrentPage"
                         :total-pages="finishedTotalPages"
                         :total-count="finishedTotalCount"
                         :page-size="finishedPageSize"
                         :show-status-filters="false"
                         preference-key="dashboard.finishedTasks"
                         @update="fetchFinishedTasks"
          />
        </div>

        <div class="grid-item task-list area-planned">
          <div style="height:10px" />
          <PaginatedList :tasks="plannedServiceVisits"
                         :header-text="$t('dashboardPage.plannedServiceVisits')"
                         show-header="true"
                         :table-columns="plannedVisitColumns"
                         :loading="isPlannedLoading"
                         :current-page="plannedCurrentPage"
                         :total-pages="plannedTotalPages"
                         :total-count="plannedTotalCount"
                         :page-size="plannedPageSize"
                         :show-status-filters="false"
                         preference-key="dashboard.plannedServiceVisits"
                         @update="fetchPlannedServiceVisits"
          />
        </div>
        <div class="grid-item task-list area-finished-visits">
          <div style="height:10px" />
          <PaginatedList :tasks="finishedServiceVisits"
                         :header-text="$t('dashboardPage.finishedServiceVisits')"
                         show-header="true"
                         :table-columns="finishedVisitColumns"
                         :loading="isFinishedVisitsLoading"
                         :current-page="finishedVisitsCurrentPage"
                         :total-pages="finishedVisitsTotalPages"
                         :total-count="finishedVisitsTotalCount"
                         :page-size="finishedVisitsPageSize"
                         :show-status-filters="false"
                         preference-key="dashboard.finishedServiceVisits"
                         @update="fetchFinishedServiceVisits"
          />
        </div>

        <div class="grid-item contact-info area-inform">
          <p><span v-html="companyInformation?.Information" /></p>
        </div>
        <div class="grid-item contact-info area-contact">
          <p><span v-html="companyInformation?.Contact" /></p>
        </div>
      </div>
    </div>
  </main>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import PaginatedList from '@/components/task/PaginatedList.vue'
import MainButton from '@/components/buttons/MainButton.vue'
import ReportList from '@/components/document/ReportList.vue'
import SearchBox from '@/components/inputs/SearchBox.vue'
import router from '@/router'
import i18n from '../locales/i18n'
import { isReportEnvironment, withUkTaskColumns } from '@/utils/environment.js'
import { loadPreference, savePreference, loadListPreference } from '@/utils/preferences.js'

import { GetTasks } from '@/api/task.js'
import { useToast } from 'vue-toastification'
import { useStore } from 'vuex'

const toast = useToast()
const store = useStore()

const activeTasks = ref([])
const finishedTasks = ref([])
const plannedServiceVisits = ref([])
const finishedServiceVisits = ref([])
// Pagination state

// Active Tasks Pagination
const activeCurrentPage = ref(1)
const activeTotalPages = ref(1)
const activeTotalCount = ref(0)
const activePageSize = ref(20)
const isActiveLoading = ref(false)

const finishedPageSize = ref(20)
const finishedCurrentPage = ref(1)
const finishedTotalPages = ref(1)
const finishedTotalCount = ref(0)
const isFinishedLoading = ref(false)

const plannedPageSize = ref(20)
const plannedCurrentPage = ref(1)
const plannedTotalPages = ref(1)
const plannedTotalCount = ref(0)
const isPlannedLoading = ref(false)

const finishedVisitsPageSize = ref(20)
const finishedVisitsCurrentPage = ref(1)
const finishedVisitsTotalPages = ref(1)
const finishedVisitsTotalCount = ref(0)
const isFinishedVisitsLoading = ref(false)

const companyInformation = ref(null)
const showReports = isReportEnvironment()
const isReadOnly = computed(() => store.getters['account/isReadOnly'])

// UK/GB02 show which tasks have a report, and their customer, in columns of their own
const activeTaskColumns = withUkTaskColumns(['Nr', 'CreatedDate', 'Priority', 'Status', 'TaskDescription', 'Object1'])
const finishedTaskColumns = withUkTaskColumns(['Nr', 'FinishedDate', 'Status', 'TaskDescription', 'Object1'])
const plannedVisitColumns = withUkTaskColumns(['Nr', 'PlannedDate', 'Contract', 'TaskDescription', 'Object1'])
const finishedVisitColumns = withUkTaskColumns(['Nr', 'FinishedDate', 'Contract', 'TaskDescription', 'Object1'])

// Task search (UK/GB02) applies to all four lists. Each list's last paging and sorting is kept for a new search.
// The search is saved per user in the browser and used again in the next session.
const taskSearchPreference = 'dashboardTaskSearch'
const taskSearch = ref(showReports ? (loadPreference(taskSearchPreference) || '') : '')
const lastListParams = { active: {}, finished: {}, planned: {}, finishedVisits: {} }

function onTaskSearch (search) {
  savePreference(taskSearchPreference, search)
  fetchActiveTasks({ ...lastListParams.active, page: 1 })
  fetchFinishedTasks({ ...lastListParams.finished, page: 1 })
  fetchPlannedServiceVisits({ ...lastListParams.planned, page: 1 })
  fetchFinishedServiceVisits({ ...lastListParams.finishedVisits, page: 1 })
}

const sortColumnApiMapping = {
  Customer: 'C.Name',
  CreatedBy: 'CB.Name',
  Status: 'T.Status',
  Object1: 'O.Name',
  Owner: 'T.OwnerSign',
  Priority: 'T.Priority',
  TaskDescription: 'SE.Value',
  FinishedDate: 'FinishedDate',
  Contract: 'CO.ContractNr'
}
async function fetchTasksWithFilter (filter) {
  console.log(filter)
  try {
    const params = new URLSearchParams()

    params.append('filter.page', filter.page ?? 0)
    params.append('filter.pageSize', filter.pageSize || 20)
    params.append('filter.orderBy', filter.orderBy || 'PlannedDate')
    params.append('filter.descending', filter.descending || false)
    if (filter.search) {
      params.append('filter.search', filter.search)
    }

    ;(filter.taskStatuses || []).forEach(status => {
      params.append('filter.taskStatuses', status)
    })

    if (filter.includeOfferingType) {
      const include = Array.isArray(filter.includeOfferingType)
        ? filter.includeOfferingType
        : [filter.includeOfferingType]
      include.forEach(type => {
        params.append('filter.UPFilters', `10073:EQ:${type}`)
      })
    }

    if (filter.excludeOfferingTypes) {
      const exclude = Array.isArray(filter.excludeOfferingTypes)
        ? filter.excludeOfferingTypes
        : [filter.excludeOfferingTypes]
      exclude.forEach(type => {
        params.append('filter.UPFilters', `10073:NEQ:${type}`)
      })
    }
    console.log('filter')
    console.log(filter)
    const response = await GetTasks(`?${params.toString()}`)
    console.log(response)
    if (response.status === 200) {
      return {
        tasks: response.data.Tasks || [],
        count: response.data.Count || 0
      }
    } else {
      return { tasks: [], Count: 0 }
    }
  } catch (error) {
    toast.warning(i18n.global.t('taskPage.couldNotGetData'))
    return { tasks: [], Count: 0 }
  }
}

const fetchActiveTasks = async ({ page, pageSize: sizeParam, sortColumn, sortOrder, statuses }) => {
  isActiveLoading.value = true
  lastListParams.active = { page, pageSize: sizeParam, sortColumn, sortOrder }

  if (sizeParam && sizeParam !== activePageSize.value) {
    activePageSize.value = sizeParam
  }
  if (page && page !== activeCurrentPage.value) {
    activeCurrentPage.value = page
  }
  try {
    const { tasks, count } = await fetchTasksWithFilter({
      search: taskSearch.value.trim(),
      taskStatuses: [0, 1, 2, 3],
      excludeOfferingTypes: ['22 - Service contract', '23 - Energy and climate contract'],
      orderBy: sortColumn && sortColumnApiMapping[sortColumn]
        ? sortColumnApiMapping[sortColumn]
        : (sortColumn || 'CreatedDate'),
      descending: sortOrder === 'desc',
      page: activeCurrentPage.value - 1,
      pageSize: activePageSize.value
    })
    activeTasks.value = tasks
    activeTotalCount.value = count
    activeTotalPages.value = Math.ceil(count / activePageSize.value)
  } finally {
    isActiveLoading.value = false
  }
}

const fetchFinishedTasks = async ({ page, pageSize: sizeParam, sortColumn, sortOrder, statuses }) => {
  isFinishedLoading.value = true
  lastListParams.finished = { page, pageSize: sizeParam, sortColumn, sortOrder }

  if (sizeParam && sizeParam !== finishedPageSize.value) {
    finishedPageSize.value = sizeParam
  }
  if (page && page !== finishedCurrentPage.value) {
    finishedCurrentPage.value = page
  }
  try {
    const { tasks, count } = await fetchTasksWithFilter({
      search: taskSearch.value.trim(),
      taskStatuses: [4, 5],
      excludeOfferingTypes: ['22 - Service contract', '23 - Energy and climate contract'],
      orderBy: sortColumn && sortColumnApiMapping[sortColumn]
        ? sortColumnApiMapping[sortColumn]
        : (sortColumn || 'FinishedDate'),
      descending: sortOrder === 'desc',
      page: finishedCurrentPage.value - 1,
      pageSize: finishedPageSize.value
    })
    finishedTotalCount.value = count
    finishedTotalPages.value = Math.ceil(count / finishedPageSize.value)
    finishedTasks.value = tasks
  } finally {
    isFinishedLoading.value = false
  }
}

const fetchPlannedServiceVisits = async ({ page, pageSize: sizeParam, sortColumn, sortOrder, statuses }) => {
  isPlannedLoading.value = true
  lastListParams.planned = { page, pageSize: sizeParam, sortColumn, sortOrder }

  if (sizeParam && sizeParam !== plannedPageSize.value) {
    plannedPageSize.value = sizeParam
  }
  if (page && page !== plannedCurrentPage.value) {
    plannedCurrentPage.value = page
  }
  try {
    const { tasks, count } = await fetchTasksWithFilter({
      search: taskSearch.value.trim(),
      taskStatuses: [0, 1, 2, 3],
      includeOfferingType: ['22 - Service contract'],
      orderBy: sortColumn && sortColumnApiMapping[sortColumn]
        ? sortColumnApiMapping[sortColumn]
        : (sortColumn || 'PlanStartDate'),
      descending: sortOrder === 'desc',
      page: plannedCurrentPage.value - 1,
      pageSize: plannedPageSize.value
    })
    plannedServiceVisits.value = tasks
    plannedTotalCount.value = count
    plannedTotalPages.value = Math.ceil(count / plannedPageSize.value)
  } finally {
    isPlannedLoading.value = false
  }
}

const fetchFinishedServiceVisits = async ({ page, pageSize: sizeParam, sortColumn, sortOrder, statuses }) => {
  isFinishedVisitsLoading.value = true
  lastListParams.finishedVisits = { page, pageSize: sizeParam, sortColumn, sortOrder }

  if (sizeParam && sizeParam !== finishedVisitsPageSize.value) {
    finishedVisitsPageSize.value = sizeParam
  }
  if (page && page !== finishedVisitsCurrentPage.value) {
    finishedVisitsCurrentPage.value = page
  }

  try {
    const { tasks, count } = await fetchTasksWithFilter({
      search: taskSearch.value.trim(),
      taskStatuses: [4, 5],
      includeOfferingType: '22 - Service contract',
      orderBy: sortColumn && sortColumnApiMapping[sortColumn]
        ? sortColumnApiMapping[sortColumn]
        : (sortColumn || 'FinishedDate'),
      descending: sortOrder === 'desc',
      page: finishedVisitsCurrentPage.value - 1,
      pageSize: finishedVisitsPageSize.value
    })
    finishedServiceVisits.value = tasks
    finishedVisitsTotalCount.value = count
    finishedVisitsTotalPages.value = Math.ceil(count / finishedVisitsPageSize.value)
  } finally {
    isFinishedVisitsLoading.value = false
  }
}

// First fetch of a list, with the sorting and rows per page saved from an earlier session (UK/GB02)
function savedListParams (preferenceKey, columns, pageSize, defaultSortColumn) {
  const saved = loadListPreference(preferenceKey, columns.filter(column => column !== 'Report'))
  return {
    page: 1,
    pageSize: saved?.pageSize ?? pageSize,
    sortColumn: saved?.sortColumn ?? defaultSortColumn,
    sortOrder: saved?.sortOrder ?? 'asc'
  }
}

onMounted(() => {
  store.commit('sidebar/setCurrentPageHeaderText', i18n.global.t('taskPage.Task'))

  fetchActiveTasks(savedListParams('dashboard.activeTasks', activeTaskColumns, activePageSize.value, 'CreatedDate'))

  fetchFinishedTasks(savedListParams('dashboard.finishedTasks', finishedTaskColumns, finishedPageSize.value, 'FinishedDate'))

  fetchPlannedServiceVisits(savedListParams('dashboard.plannedServiceVisits', plannedVisitColumns, plannedPageSize.value, 'PlanStartDate'))

  fetchFinishedServiceVisits(savedListParams('dashboard.finishedServiceVisits', finishedVisitColumns, finishedVisitsPageSize.value, 'FinishedDate'))

  store.dispatch('account/getCompanyInformation')
    .then(() => {
      companyInformation.value = store.state.account.companyInformation
    })
    .catch(() => {
      toast.error(i18n.global.t('taskPage.couldNotGetCompanyInfo'))
    })
})
function newTask () {
  router.push('/create/task/')
}
</script>

<style lang="scss" scoped>
.task-page {
  height: 100vh;
  overflow-y: scroll;
  font-family: 'Lexend', sans-serif;
  text-align: center;
  color: #2c3e50;
  background: linear-gradient(90deg, var(--taskBackgroundColor1) 0%, var(--taskBackgroundColor2) 10px);
}

.dashboard-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.grid-item.task-list {
  padding: 0 1rem;
  max-height: 593px;
  overflow-y: auto;
}

.grid-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(3, 1fr);
  gap: 1rem;
  padding: 1rem;
}

// The sections wrapper takes no part in the layout, its sections are the grid items
.dashboard-sections {
  display: contents;
}

// UK/GB02: a full width row of reports first, the task lists and information below it as before.
// The reports row is taller than the others, so rows size to their own content.
.grid-container.with-reports {
  grid-template-rows: none;
  grid-auto-rows: auto;
}

.grid-item.report-section {
  grid-column: 1 / -1;
  max-height: none;
  overflow: visible;
  background-color: var(--alternatingListColor);
}

@media screen and (max-width: 900px) {
  .grid-container {
      grid-template-columns: 1fr;
  }
}

.grid-item {
  border: 1px solid #ccc;
  padding: 1rem;
  max-height: 310px;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
}

.task-list {
  background-color: var(--alternatingListColor);
  overflow-y: auto;
  max-height: 300px;
}

.contact-info {
  background-color: var(--alternatingListColor);
}

.container {
  padding: 1rem;
}

.styled-table {
  table-layout: fixed;

  border-collapse: collapse;
  margin: 5px 0;
  font-size: 0.9em;
  font-family: 'Lexend', sans-serif;
  width: 100%;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
}

.styled-table thead tr {
  background-color: var(--mainBlue);
  color: white;
  text-align: left;
}

</style>
